import { useState, useEffect, type ChangeEvent } from 'react';
import { useI18n } from '../../../../i18n';
import { useAuth } from '../../../../context/AuthContext';
import { AdminCard } from '../components/AdminCard';
import { AdminToggle } from '../components/AdminToggle';
import { AdminBadge } from '../components/AdminBadge';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { adminRequest, getToken } from '../../../shared/services/api';

// ═══════════════════════════════════════════════════════════════
// AdminEmpresa — Tab "Empresa": brand, regional, templates, etc.
// ═══════════════════════════════════════════════════════════════

// Multipart upload usa fetch directo + Bearer manual (adminRequest fija
// Content-Type: application/json y rompe el boundary multipart).
const ADMIN_BASE = '/api/v1/admin';

interface TenantConfig {
  id: string;
  nombre: string;
  nombreMarca: string | null;
  logoUrl: string | null;
  logoHeaderUrl: string | null;
  faviconUrl: string | null;
  colorPrimario: string;
  colorSecundario: string;
  colorAcento: string;
  fuente: string;
  idiomaPredeterminado: string;
  zonaHoraria: string;
  formatoFecha: string;
  dominioPersonalizado: string | null;
  // 0573: persistencia governance + auth en jsonb libre
  configuracion?: Record<string, unknown>;
}

type PillKey = 'brand' | 'regional' | 'governance' | 'auth';

const PILL_STYLE_ACTIVE: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  backgroundColor: '#1F3864',
  color: '#fff',
  fontFamily: 'inherit',
};

const PILL_STYLE_INACTIVE: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  backgroundColor: '#fff',
  color: '#6b7280',
  fontFamily: 'inherit',
};

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '8px 14px',
  border: '1px solid #d5d5d5',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
};

const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: 'rgba(0,0,0,0.54)',
  marginBottom: 4,
};

const BTN_SAVE: React.CSSProperties = {
  padding: '8px 20px',
  backgroundColor: '#0E243E',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

export function AdminEmpresa() {
  const { t } = useI18n();
  // 0581: gate canEdit por rol (defensa en profundidad: el filtro de tabs ya excluye
  // TENANT_AUDITOR/MEMBER, pero el componente debe ser robusto si el JWT cambia).
  const { user } = useAuth();
  const role = user?.role ?? 'MEMBER';
  const canEdit =
    role === 'SUPERADMIN_PLATFORM' || role === 'TENANT_OWNER' || role === 'TENANT_ADMIN';
  const [activePill, setActivePill] = useState<PillKey>('brand');
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadTenant = async () => {
    try {
      setLoading(true);
      const json = await adminRequest<{ data: TenantConfig }>('/tenant');
      setTenant(json.data);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenant();
  }, []);

  const saveTenant = async (data: Partial<TenantConfig>) => {
    try {
      setSaving(true);
      const json = await adminRequest<{ data: TenantConfig }>('/tenant', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      setTenant(json.data);
      setMessage({ type: 'success', text: t.admin.config.configGuardada });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (endpoint: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      // Multipart: NO adminRequest (forzaría Content-Type: application/json y
      // rompería el boundary). Bearer manual desde localStorage para cross-subdomain
      // Vercel+Railway (commit 571f88dd, fix [0540] -> 0541).
      const token = getToken();
      const res = await fetch(`${ADMIN_BASE}${endpoint}`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        // NO Content-Type — browser sets multipart boundary
      });
      if (!res.ok) throw new Error(t.admin.config.errorAlSubirArchivo);
      await loadTenant();
      setMessage({ type: 'success', text: t.admin.config.archivoActualizado });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  // 0573: pill 'templates' eliminada — los docs se generan via M4 sin plantillas Word
  const pills: { key: PillKey; label: string }[] = [
    { key: 'brand', label: t.admin.empresa.pills.brand },
    { key: 'regional', label: t.admin.empresa.pills.regional },
    { key: 'governance', label: t.admin.empresa.pills.governance },
    { key: 'auth', label: t.admin.empresa.pills.auth },
  ];

  if (loading || !tenant) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <LoadingSpinner text={t.common.cargando} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 24 }}>
      {/* Left sidebar pills */}
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180, flexShrink: 0 }}
      >
        {pills.map((p) => (
          <button
            key={p.key}
            onClick={() => setActivePill(p.key)}
            style={activePill === p.key ? PILL_STYLE_ACTIVE : PILL_STYLE_INACTIVE}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Right content area */}
      <div style={{ flex: 1, maxWidth: 720 }}>
        {!canEdit && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 16px',
              borderRadius: 8,
              fontSize: 13,
              backgroundColor: '#EFF6FF',
              color: '#1E40AF',
              border: '1px solid #BFDBFE',
            }}
          >
            {t.admin.usuarios.empresaReadOnly}
          </div>
        )}
        {message && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 16px',
              borderRadius: 8,
              fontSize: 14,
              backgroundColor: message.type === 'success' ? '#F0FDF4' : '#FEF2F2',
              color: message.type === 'success' ? '#16A34A' : '#DC2626',
              border: `1px solid ${message.type === 'success' ? '#BBF7D0' : '#FECACA'}`,
            }}
          >
            {message.text}
          </div>
        )}

        {activePill === 'brand' && (
          <BrandContent
            tenant={tenant}
            onSave={saveTenant}
            onUpload={uploadFile}
            saving={saving}
            canEdit={canEdit}
          />
        )}
        {activePill === 'regional' && (
          <RegionalContent tenant={tenant} onSave={saveTenant} saving={saving} canEdit={canEdit} />
        )}
        {activePill === 'governance' && (
          <GovernanceContent
            tenant={tenant}
            onSave={saveTenant}
            saving={saving}
            canEdit={canEdit}
          />
        )}
        {activePill === 'auth' && (
          <AuthContent tenant={tenant} onSave={saveTenant} saving={saving} canEdit={canEdit} />
        )}
      </div>
    </div>
  );
}

// ─── Brand ───

function BrandContent({
  tenant,
  onSave,
  onUpload,
  saving,
  canEdit,
}: {
  tenant: TenantConfig;
  onSave: (data: Partial<TenantConfig>) => Promise<void>;
  onUpload: (endpoint: string, file: File) => Promise<void>;
  saving: boolean;
  canEdit: boolean;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    nombreMarca: tenant.nombreMarca ?? '',
    colorPrimario: tenant.colorPrimario,
    colorSecundario: tenant.colorSecundario,
    colorAcento: tenant.colorAcento,
    fuente: tenant.fuente,
  });

  const handleFile = (endpoint: string) => (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(endpoint, file);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <AdminCard title={t.admin.config.marca}>
        <div style={{ marginBottom: 12 }}>
          <label style={LABEL_STYLE}>{t.admin.config.nombreMarca}</label>
          <input
            type="text"
            style={INPUT_STYLE}
            value={form.nombreMarca}
            onChange={(e) => setForm({ ...form, nombreMarca: e.target.value })}
          />
        </div>
        <div>
          <label style={LABEL_STYLE}>{t.admin.config.fuente}</label>
          <input
            type="text"
            style={INPUT_STYLE}
            value={form.fuente}
            placeholder="Montserrat"
            onChange={(e) => setForm({ ...form, fuente: e.target.value })}
          />
        </div>
      </AdminCard>

      <AdminCard title={t.admin.config.colores}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {[
            { key: 'colorPrimario', label: t.admin.config.primario },
            { key: 'colorSecundario', label: t.admin.config.secundario },
            { key: 'colorAcento', label: t.admin.config.acento },
          ].map(({ key, label }) => (
            <div key={key}>
              <label style={LABEL_STYLE}>{label}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="color"
                  value={(form as any)[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  style={{
                    width: 40,
                    height: 40,
                    border: '1px solid #d5d5d5',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                />
                <input
                  type="text"
                  value={(form as any)[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  style={{
                    flex: 1,
                    border: '1px solid #d5d5d5',
                    borderRadius: 8,
                    padding: '4px 8px',
                    fontSize: 14,
                    fontFamily: 'monospace',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16, padding: 12, border: '1px solid #d5d5d5', borderRadius: 8 }}>
          <p style={{ fontSize: 12, color: 'rgba(0,0,0,0.54)', marginBottom: 8 }}>
            {t.admin.config.vistaPrevia}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ height: 40, flex: 1, borderRadius: 4, background: form.colorPrimario }} />
            <div
              style={{ height: 40, flex: 1, borderRadius: 4, background: form.colorSecundario }}
            />
            <div style={{ height: 40, flex: 1, borderRadius: 4, background: form.colorAcento }} />
          </div>
        </div>
      </AdminCard>

      <AdminCard title={t.admin.config.logos}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          <div>
            <label style={LABEL_STYLE}>{t.admin.config.logoPrincipal}</label>
            {tenant.logoUrl && (
              <img
                src={tenant.logoUrl}
                alt="Logo"
                style={{ height: 40, marginBottom: 8, objectFit: 'contain' }}
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleFile('/tenant/logo')}
              style={{ fontSize: 12 }}
            />
          </div>
          <div>
            <label style={LABEL_STYLE}>{t.admin.config.logoHeader}</label>
            {tenant.logoHeaderUrl && (
              <img
                src={tenant.logoHeaderUrl}
                alt="Logo header"
                style={{ height: 40, marginBottom: 8, objectFit: 'contain' }}
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleFile('/tenant/logo-header')}
              style={{ fontSize: 12 }}
            />
          </div>
          <div>
            <label style={LABEL_STYLE}>{t.admin.config.favicon}</label>
            {tenant.faviconUrl && (
              <img
                src={tenant.faviconUrl}
                alt="Favicon"
                style={{ height: 32, marginBottom: 8, objectFit: 'contain' }}
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleFile('/tenant/favicon')}
              style={{ fontSize: 12 }}
            />
          </div>
        </div>
      </AdminCard>

      <button
        onClick={() => onSave(form)}
        disabled={saving || !canEdit}
        style={{ ...BTN_SAVE, opacity: saving ? 0.5 : 1, alignSelf: 'flex-start' }}
      >
        {saving ? t.admin.config.guardando : t.admin.config.guardarCambios}
      </button>
    </div>
  );
}

// ─── Regional ───

function RegionalContent({
  tenant,
  onSave,
  saving,
  canEdit,
}: {
  tenant: TenantConfig;
  onSave: (data: Partial<TenantConfig>) => Promise<void>;
  saving: boolean;
  canEdit: boolean;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    idiomaPredeterminado: tenant.idiomaPredeterminado,
    zonaHoraria: tenant.zonaHoraria,
    formatoFecha: tenant.formatoFecha ?? 'DD/MM/YYYY',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <AdminCard title={t.admin.empresa.pills.regional}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={LABEL_STYLE}>{t.admin.config.idioma}</label>
            <select
              style={INPUT_STYLE}
              value={form.idiomaPredeterminado}
              onChange={(e) => setForm({ ...form, idiomaPredeterminado: e.target.value })}
            >
              <option value="es">Español</option>
              <option value="en">English</option>
              <option value="zh">中文</option>
            </select>
          </div>
          <div>
            <label style={LABEL_STYLE}>{t.admin.config.zonaHoraria}</label>
            <select
              style={INPUT_STYLE}
              value={form.zonaHoraria}
              onChange={(e) => setForm({ ...form, zonaHoraria: e.target.value })}
            >
              <option value="Europe/Madrid">Europe/Madrid</option>
              <option value="America/New_York">America/New York</option>
              <option value="America/Mexico_City">America/Mexico City</option>
              <option value="America/Bogota">America/Bogota</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
        </div>
        <div>
          <label style={LABEL_STYLE}>{t.admin.empresa.formatoFecha}</label>
          <select
            style={INPUT_STYLE}
            value={form.formatoFecha}
            onChange={(e) => setForm({ ...form, formatoFecha: e.target.value })}
          >
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>
      </AdminCard>

      <button
        onClick={() => onSave(form as any)}
        disabled={saving || !canEdit}
        style={{ ...BTN_SAVE, opacity: saving ? 0.5 : 1, alignSelf: 'flex-start' }}
      >
        {saving ? t.admin.config.guardando : t.admin.config.guardarCambios}
      </button>
    </div>
  );
}

// ─── Governance ───
// 0573: persistencia via tenant.configuracion.gobernanza jsonb.

function GovernanceContent({
  tenant,
  onSave,
  saving,
  canEdit,
}: {
  tenant: TenantConfig;
  onSave: (data: Partial<TenantConfig>) => Promise<void>;
  saving: boolean;
  canEdit: boolean;
}) {
  const cfgRaw = (tenant as { configuracion?: { gobernanza?: Record<string, string> } })
    .configuracion;
  const initial = cfgRaw?.gobernanza ?? {};
  const [sector, setSector] = useState(initial.sector ?? 'construccion');
  const [cicloRevision, setCicloRevision] = useState(initial.cicloRevision ?? '12');
  const [docInstrucciones, setDocInstrucciones] = useState(
    initial.docInstrucciones ?? 'PE.21.IT.02',
  );

  const handleSave = () => {
    const cfg = (tenant as { configuracion?: Record<string, unknown> }).configuracion ?? {};
    const next = {
      ...cfg,
      gobernanza: { sector, cicloRevision, docInstrucciones },
    } as Record<string, unknown>;
    onSave({ configuracion: next as never });
  };

  const fieldInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid #D9D9D9',
    borderRadius: 6,
    fontSize: 11,
    fontFamily: 'inherit',
  };

  return (
    <AdminCard title="Gobernanza" accent="#16507D" style={{ maxWidth: 450 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Sector */}
        <div>
          <label style={LABEL_STYLE}>Sector</label>
          <select
            style={fieldInputStyle}
            value={sector}
            onChange={(e) => setSector(e.target.value)}
          >
            <option value="construccion">Construccion</option>
            <option value="industria">Industria</option>
            <option value="servicios">Servicios</option>
            <option value="tecnologia">Tecnologia</option>
          </select>
        </div>

        {/* Ciclo revision */}
        <div>
          <label style={LABEL_STYLE}>Ciclo de revision</label>
          <select
            style={fieldInputStyle}
            value={cicloRevision}
            onChange={(e) => setCicloRevision(e.target.value)}
          >
            <option value="6">6 meses</option>
            <option value="12">12 meses</option>
            <option value="24">24 meses</option>
          </select>
        </div>

        {/* Documento instrucciones */}
        <div>
          <label style={LABEL_STYLE}>Documento de instrucciones</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              style={{ ...fieldInputStyle, flex: 1 }}
              value={docInstrucciones}
              onChange={(e) => setDocInstrucciones(e.target.value)}
            />
            <button
              onClick={() => {
                /* TODO 0497: implementar visor de documento (docInstrucciones) — handler no operativo */
              }}
              style={{
                padding: '6px 12px',
                fontSize: 11,
                fontWeight: 600,
                background: '#2F5496',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              Ver
            </button>
          </div>
        </div>

        {/* 0573: Boton guardar gobernanza */}
        <button
          onClick={handleSave}
          disabled={saving || !canEdit}
          style={{
            marginTop: 8,
            padding: '8px 16px',
            fontSize: 12,
            fontWeight: 600,
            background: saving ? '#9CA3AF' : '#1F3864',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: saving ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            alignSelf: 'flex-start',
          }}
        >
          {saving ? 'Guardando...' : 'Guardar gobernanza'}
        </button>
      </div>
    </AdminCard>
  );
}

// ─── Auth ───
// 0573: persistencia via tenant.configuracion.auth jsonb.

function AuthContent({
  tenant,
  onSave,
  saving,
  canEdit,
}: {
  tenant: TenantConfig;
  onSave: (data: Partial<TenantConfig>) => Promise<void>;
  saving: boolean;
  canEdit: boolean;
}) {
  const cfgRaw = (
    tenant as {
      configuracion?: {
        auth?: {
          proveedor?: string;
          clientId?: string;
          entraTenantId?: string;
          scimEnabled?: boolean;
          adGroups?: { group: string; role: string }[];
        };
      };
    }
  ).configuracion;
  const initial = cfgRaw?.auth ?? {};

  const [proveedor, setProveedor] = useState(initial.proveedor ?? 'email');
  const [clientId, setClientId] = useState(initial.clientId ?? '');
  const [entraTenantId, setEntraTenantId] = useState(initial.entraTenantId ?? '');
  const [scimEnabled, setScimEnabled] = useState(initial.scimEnabled ?? false);
  const [adGroups, setAdGroups] = useState<{ group: string; role: string }[]>(
    initial.adGroups ?? [
      { group: 'Direccion', role: 'TENANT_OWNER' },
      { group: 'Mandos intermedios', role: 'TENANT_ADMIN' },
      { group: 'Tecnicos', role: 'MEMBER' },
      { group: 'Resto', role: 'MEMBER' },
    ],
  );

  const handleSaveAuth = () => {
    const cfg = (tenant as { configuracion?: Record<string, unknown> }).configuracion ?? {};
    const next = {
      ...cfg,
      auth: { proveedor, clientId, entraTenantId, scimEnabled, adGroups },
    } as Record<string, unknown>;
    onSave({ configuracion: next as never });
  };

  const fieldInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid #D9D9D9',
    borderRadius: 6,
    fontSize: 11,
    fontFamily: 'inherit',
  };

  const addMapping = () => {
    setAdGroups([...adGroups, { group: '', role: 'MEMBER' }]);
  };

  return (
    <AdminCard title="Autenticacion" accent="#1A94BF" style={{ maxWidth: 480 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Proveedor */}
        <div>
          <label style={LABEL_STYLE}>Proveedor</label>
          <select
            style={fieldInputStyle}
            value={proveedor}
            onChange={(e) => setProveedor(e.target.value)}
          >
            <option value="email">Email + contrasena</option>
            <option value="entra">Microsoft Entra ID</option>
            <option value="google">Google Workspace</option>
          </select>
        </div>

        {/* Microsoft Entra ID block */}
        <div style={{ background: '#F2F2F2', borderRadius: 6, padding: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#1F3864', marginBottom: 10 }}>
            Microsoft Entra ID
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label style={LABEL_STYLE}>Client ID</label>
              <input
                type="text"
                style={{ ...fieldInputStyle, fontFamily: 'monospace' }}
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </div>
            <div>
              <label style={LABEL_STYLE}>Tenant ID</label>
              <input
                type="text"
                style={{ ...fieldInputStyle, fontFamily: 'monospace' }}
                value={entraTenantId}
                onChange={(e) => setEntraTenantId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </div>
          </div>
        </div>

        {/* AD groups mapping */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#1F3864', marginBottom: 10 }}>
            Mapeo de grupos AD
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {adGroups.map((mapping, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  style={{ ...fieldInputStyle, flex: 1 }}
                  value={mapping.group}
                  onChange={(e) => {
                    const updated = [...adGroups];
                    updated[i] = { ...updated[i], group: e.target.value };
                    setAdGroups(updated);
                  }}
                  placeholder="Nombre del grupo"
                />
                <select
                  style={{ ...fieldInputStyle, flex: 0, width: 180 }}
                  value={mapping.role}
                  onChange={(e) => {
                    const updated = [...adGroups];
                    updated[i] = { ...updated[i], role: e.target.value };
                    setAdGroups(updated);
                  }}
                >
                  <option value="TENANT_OWNER">TENANT_OWNER</option>
                  <option value="TENANT_ADMIN">TENANT_ADMIN</option>
                  <option value="TENANT_AUDITOR">TENANT_AUDITOR</option>
                  <option value="MEMBER">MEMBER</option>
                </select>
              </div>
            ))}
          </div>
          <button
            onClick={addMapping}
            style={{
              marginTop: 8,
              padding: '4px 12px',
              fontSize: 11,
              fontWeight: 500,
              background: 'none',
              color: '#2F5496',
              border: '1px dashed #2F5496',
              borderRadius: 6,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            + Anadir mapeo
          </button>
        </div>

        {/* SCIM */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 4 }}>
          <AdminToggle checked={scimEnabled} onChange={setScimEnabled} disabled />
          <span style={{ fontSize: 12, color: '#1F3864' }}>SCIM provisioning</span>
          <AdminBadge bg="rgba(245,158,11,0.15)" color="#B45309">
            Premium
          </AdminBadge>
        </div>

        {/* 0573: Boton guardar autenticacion */}
        <button
          onClick={handleSaveAuth}
          disabled={saving || !canEdit}
          style={{
            marginTop: 8,
            padding: '8px 16px',
            fontSize: 12,
            fontWeight: 600,
            background: saving ? '#9CA3AF' : '#1F3864',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: saving ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            alignSelf: 'flex-start',
          }}
        >
          {saving ? 'Guardando...' : 'Guardar configuracion auth'}
        </button>
      </div>
    </AdminCard>
  );
}
